import React, {useEffect} from 'react';
import {StyleSheet, TextStyle, View, ViewStyle} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {profileData} from 'redux/selectors/profileSelector';
import {getProfileDetailsRequest} from 'redux/slices/myProfileSlice';
//ui related imports
import colors from 'theme/Colors';
import {spacing} from 'theme/Units';
import {ActionButton, Text} from 'ui-kits';

import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import {useResponsiveStyles} from 'hooks/useResponsiveStyles';
import {getFormattedDate} from 'utils/getFormattedDate';

type infoRow = {
  label1: string;
  value1: string;
  label2: string;
  value2: string;
};

const InfoRow = ({label1, value1, label2, value2}: infoRow) => {
  const styles = useResponsiveStyles<styleTypes>(baseStyles, responsive);
  return (
    <View style={styles.row}>
      <View style={styles.innerRow}>
        <Text variant="sourcesans16m" style={styles.label}>
          {label1}
        </Text>
        <Text variant="sourcesans16" style={styles.value}>
          {value1}
        </Text>
      </View>
      <View style={styles.innerRow}>
        <Text variant="sourcesans16m" style={styles.label}>
          {label2}
        </Text>
        <Text variant="sourcesans16" style={styles.value}>
          {value2}
        </Text>
      </View>
    </View>
  );
};

export function ViewProfile({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const dispatch = useDispatch();
  const profile = useSelector(profileData);

  const styles = useResponsiveStyles<styleTypes>(baseStyles, responsive);

  useEffect(() => {
    dispatch(getProfileDetailsRequest());
  }, [dispatch]);

  function handleEdit() {
    navigation.navigate(Routes.EDIT_PROFILE);
  }

  function handleReset() {
    navigation.navigate(Routes.RESET_PASSWORD);
  }

  return (
    <View>
      <InfoRow
        label1="Full Name"
        value1={profile?.first_name?.concat(' ', profile?.last_name ?? '')}
        label2="Contact Number"
        value2={profile?.phone}
      />
      <InfoRow
        label1="Date Of Birth"
        value1={
          profile?.dob
            ? getFormattedDate(new Date(profile?.dob), 'dd-mm-yyyy', '/')
            : null
        }
        label2="Personal Email"
        value2={profile?.personal_email}
      />
      <InfoRow
        label1="Date Of Joining"
        value1={
          profile?.date_of_joining
            ? getFormattedDate(
                new Date(profile?.date_of_joining),
                'dd-mm-yyyy',
                '/',
              )
            : null
        }
        label2="Official Email"
        value2={profile?.email}
      />
      <View style={styles.actionButtonWrapper}>
        <ActionButton
          iconSize={24}
          rightIcon="edit_white"
          title="Edit Profile"
          style={{
            ...styles.actionButton,
            ...styles.editActionButton,
          }}
          textStyle={styles.editActionButtonText}
          onPress={handleEdit}
        />
        <ActionButton
          iconSize={24}
          rightIcon="reset_password_blue"
          title="Reset Password"
          style={styles.actionButton}
          variant="secondary"
          onPress={handleReset}
        />
      </View>
    </View>
  );
}

type styleTypes = {
  editActionButtonText: TextStyle;
  editActionButton: ViewStyle;
  row: ViewStyle;
  innerRow: ViewStyle;
  label: TextStyle;
  value: TextStyle;
  actionButtonWrapper: ViewStyle;
  actionButton: ViewStyle;
};

const baseStyles = StyleSheet.create({
  editActionButtonText: {color: colors.palette.white},
  editActionButton: {backgroundColor: colors.palette.brandBlue},
  row: {
    flexDirection: 'row',
    marginBottom: spacing['8'],
    maxWidth: 860,
  },
  innerRow: {
    flexDirection: 'row',
    gap: spacing['2'],
  },
  label: {
    width: 150,
  },
  value: {
    minWidth: 200,
    maxWidth: 'auto',
    alignSelf: 'flex-start',
    color: colors.palette.secondaryBlack5,
  },
  actionButtonWrapper: {
    flexDirection: 'row',
    zIndex: -10,
    gap: spacing['4'],
    marginTop: spacing['2'],
  },
  actionButton: {
    width: 310,
    height: 40,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    borderWidth: 1.5,
    borderColor: colors.palette.brandBlue,
  },
});

const responsive: Record<number, Partial<styleTypes>> = {
  640: StyleSheet.create({
    row: {
      flexDirection: 'column',
      gap: spacing['4'],
      marginBottom: spacing['4'],
    },
    innerRow: {
      flexDirection: 'column',
    },
    actionButtonWrapper: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  }),
};
